import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useMutation, useSubscription, useLazyQuery } from 'react-apollo';
import { client } from '..';
import { UserFeature, UserFeatureEnum } from '../types/UserFeature';
import {
    AssessEnquiryAIMutation,
    AssessEnquiryAIResult,
    ReassessEnquiryAIMutation,
    ReassessEnquiryAIResult,
    EnquiryAIAssessmentMutationParams,
    GetEnquiryAIAssessmentQuery,
    GetEnquiryAIAssessmentData,
    GetEnquiryAIAssessmentParams,
    EnquiryAIProgressSubscription,
    EnquiryAIProgressSubscriptionData,
    DocumentAIQuestionQuery,
    DocumentAIQuestionData,
    DocumentAIQuestionParams,
    DocumentAIInteractionsQuery,
    DocumentAIInteractionsData,
    DocumentAIInteractionsParams,
    DocumentAICitation,
    EnquiryAssessmentProcessingStatus,
    EnquiryAIAssessmentRecord,
} from './DocumentAiRepository';

export interface DocumentAiChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    createdAt: string;
    citations?: DocumentAICitation[];
    /** Set on an assistant message that represents a failed answer (a saved interaction with
     * status: 2 failed) - rendered distinctly rather than as if the AI had actually replied with
     * this text. Its own retry sends the same question again with a brand new operationGuid
     * (this was a recorded failure, not a transport hiccup - reusing the old id would ask the
     * server to just replay whatever it saved against it). */
    isError?: boolean;
    /** Present on a user message - the operationGuid that request used. Retained with the
     * pending message (per the backend's own guidance) so a transport failure can retry with the
     * *same* operationGuid rather than double-submitting the question under a new one. */
    operationGuid?: string;
    /** Set on a user message whose own documentAIQuestion call failed outright (network/GraphQL
     * error, not a recorded server-side failure) - shows its own inline "Retry" using the stored
     * operationGuid above. */
    sendFailed?: boolean;
}

/** Basic identity details for the enquiry a session belongs to - explicit request to show these
 * in the chat dialog (same fields EnquiryFormDrawer.tsx's own header identity block shows) so a
 * chat that's survived closing that drawer, possibly a while ago, still reads as "whose enquiry
 * is this" at a glance. Passed in once at startProcessing/ensureStatusChecked time by the caller
 * (EnquiryFormDrawer already has this loaded - editing this exact enquiry - so no extra query is
 * needed here). */
export interface DocumentAiEnquirySummary {
    name: string;
    email: string | null;
    areaOfLaw: string | null;
}

interface DocumentAiSessionBase {
    /** Local, stable identity - `doc:<guid>` or `assessment:<enquiryGuid>` - known immediately on
     * click, before the backend has assigned this run a processingGuid/assessmentGuid. */
    key: string;
    enquiryGuid: string;
    title: string;
    enquirySummary: DocumentAiEnquirySummary;
    /** Whether the CURRENT/latest generation's run has finished - independent of hasResults, per
     * the backend's own UI guidance: during a reassessment, hasResults can stay true (the
     * previous result is still viewable) while isTerminal is false (a newer generation is
     * running). These are not mutually exclusive, so don't collapse them into one status enum -
     * see getDocumentAiDisplayState below for how the UI should read the combination. */
    isTerminal: boolean;
    hasResults: boolean;
    /** Friendly, user-facing text - never the backend's own raw errorCode directly (that's an
     * enum-like machine code, e.g. "ProcessingFailed", not something to show as-is). Only
     * meaningful when isTerminal && !hasResults (processing finished but produced nothing to
     * show - failed/cancelled). */
    errorMessage: string | null;
    /** The backend's own raw code, kept separately so the UI can still surface it in small print
     * for support reference without using it as the headline message. */
    errorCode: string | null;
    progressPercent: number | null;
    stage: string | null;
    /** Backend-composed human-readable progress text (e.g. "Analysing document 3 of 5...") -
     * the primary detail line in AiChatDialog.tsx; never reconstructed client-side. */
    progressMessage: string | null;
    /** True while progressPercent is a coarse milestone, not measured completion (the combined
     * "Assessing" call in particular parks at 85% until it finishes) - render an indeterminate
     * indicator rather than implying the number itself is still advancing. */
    isProgressIndeterminate: boolean;
    /** Last committed work/stage transition - the basis for an elapsed-time readout while the
     * backend is otherwise quiet (e.g. "2m elapsed in this stage"), not a heartbeat or proof of
     * a stall. */
    lastProgressAtUtc: string | null;
    createdAtUtc: string | null;
    completedAtUtc: string | null;
    /** Coarse enquiry-assessment step (Queued/ProcessingDocuments/Assessing/...) - drives the
     * stepper in AiChatDialog.tsx; `stage` above is the backend's own free-text label shown
     * alongside it, not used to decide which step is active since its exact wording isn't
     * guaranteed to match this enum's names. Null before the first status record ever arrives. */
    processingStatus: EnquiryAssessmentProcessingStatus | null;
    /** generation/revision of the most recent status record actually applied to this session -
     * used to ignore stale/out-of-order status-query or subscription events for the same run,
     * per the backend's own guidance on using `revision` this way. */
    generation: number;
    revision: number;
    /** Raw model JSON (EntityAIAssessment.AssessmentJson/CitationsJson) - the enquiry-wide,
     * combined assessment. Parsed defensively by AiChatDialog.tsx, since this is model-generated
     * JSON, not a guaranteed rigid shape. Only ever populated by the hydration/reload query - the
     * progress subscription doesn't carry these fields, so a progress tick never clobbers them.
     * A FULL reassessment (startProcessing's own refresh:true) clears both immediately on
     * dispatch, matching the backend's own "hides the earlier report while refreshing" behaviour
     * for that specific action - an INCREMENTAL assess leaves them in place instead, since the
     * previous result stays viewable throughout that kind of run. */
    assessmentJson: string | null;
    citationsJson: string | null;
    isMinimized: boolean;
    // Chat is not wired to a backend yet (explicit instruction - "not ready yet, I will provide
    // later") - these fields stay in the shape so AiChatDialog.tsx doesn't need reworking again
    // once it is, but sendMessage is currently a no-op and the compose box stays disabled.
    messages: DocumentAiChatMessage[];
    isSendingMessage: boolean;
}

/** The enquiry-wide assessment - a real backend concept (crm.enquiryAIAssessment). Explicit flow
 * change: per-document sessions (kind: 'document') were removed entirely - the header badge is
 * now the only AI entry point, so there's only ever this one session per enquiry, never one per
 * document. `kind: 'assessment'` is kept as a literal (rather than dropped) purely so call sites
 * that still pattern-match on it (AiChatDialog.tsx, AiChatMinimizedBar.tsx) don't need touching. */
export interface DocumentAiAssessmentSession extends DocumentAiSessionBase {
    kind: 'assessment';
    documentsTotal: number | null;
    documentsCompleted: number | null;
    documentsFailed: number | null;
    /** Simultaneous per-attachment work counts, not sequential UI steps. */
    documentsExtracting: number | null;
    documentsAnalysing: number | null;
    documentsReviewing: number | null;
    /** Completed passage analyses, not inserted vectors or PDF pages. passagesTotal stays null
     * (never 0 or a guess) until every attachment has a known passage count, and both are null
     * outside document processing (including the final combined call and every terminal state). */
    passagesCompleted: number | null;
    passagesTotal: number | null;
    /** Query-only (null while processing, and absent from every subscription snapshot) - detects
     * a changed enquiry/attachment/finding set or a failed document against the current
     * authorised snapshot. Only ever set from a fresh reload of enquiryAIAssessment, so a click
     * can tell "nothing to do" apart from "something changed, assess again" even while ready. */
    needsAssessment: boolean | null;
    /** Purely local bookkeeping (never set from backend data) - which mutation this session's
     * current/last run was actually dispatched with, so a failed run's own retry replays the
     * SAME kind rather than always defaulting to one or the other. Only startProcessing sets
     * this; applyAssessmentRecord's merges always inherit whatever it already was. */
    lastDispatchWasReassess: boolean;
    /** Explicit bug report: opening an enquiry that already has a saved assessment put a
     * floating minimized chip in AiChatMinimizedBar (visible app-wide, even after navigating
     * away) despite the user never touching the AI badge - ensureStatusChecked's own silent
     * hydration creates a session exactly like any other, defaulting isMinimized: true, and the
     * minimized bar used to filter on isMinimized alone. This instead tracks whether the user
     * has ever genuinely interacted with this session (clicked the badge, dispatched an assess/
     * reassess, or opened it) - only ever set true, never reset, so once a chip has legitimately
     * earned its place there it keeps reflecting live status updates as normal. */
    isActiveChip: boolean;
}

export type DocumentAiSession = DocumentAiAssessmentSession;

/** The five states the UI actually needs to tell apart - see this feature's own backend
 * "UI interpretation" notes: no session at all is "never processed"; isTerminal and hasResults
 * combine independently (a reassessment can be mid-run while the previous result is still open
 * for viewing), so this is deliberately not a plain 3-way status enum. */
export type DocumentAiDisplayState = 'idle' | 'processing' | 'processingWithResults' | 'ready' | 'failed';

export const getDocumentAiDisplayState = (session: DocumentAiSession | undefined): DocumentAiDisplayState => {
    if (!session) {
        return 'idle';
    }
    if (!session.isTerminal) {
        return session.hasResults ? 'processingWithResults' : 'processing';
    }
    return session.hasResults ? 'ready' : 'failed';
};

const buildAssessmentSessionKey = (enquiryGuid: string): string => `assessment:${enquiryGuid}`;

// Explicit bug report: the backend's own errorCode (e.g. "ProcessingFailed") was being shown to
// the user directly as the error message - that's a machine-readable code, not user-facing copy.
// These are the fixed, friendly messages shown instead; the raw code is kept on the session's own
// errorCode field for support reference (see AiChatDialog.tsx's error banner) rather than used as
// the headline.
const ASSESSMENT_AI_ERROR_MESSAGE = "We couldn't finish assessing this enquiry.";

// A small set of backend errorCodes that have a specific, already-customer-safe explanation
// worth showing instead of the generic message above - used only as a fallback for a state that
// arrived without the subscription's own `description` (e.g. a reload after the fact), since
// that field is normally the preferred, backend-composed wording for a given failure.
const FRIENDLY_ERROR_MESSAGES_BY_CODE: { [errorCode: string]: string } = {
    DocumentProcessingBusy: 'Another AI operation is already running for this enquiry. Please try again once it finishes.',
    EnquiryAccessDenied: 'Enquiry is unavailable or access is denied.',
    // A 30-second recovery check runs while this is processing; forty unsuccessful checks (the
    // backend's own bounded timeout) produce this terminal result rather than staying in
    // progress forever.
    DocumentProcessingTimedOut: 'This took longer than expected and timed out. Please try again.',
};

// Explicit bug report: a raw GUID (an operationGuid/assessmentGuid, not a sentence) showed up as
// the error banner's own headline text. Root cause - the subscription's own `description` field
// is normally already customer-safe, but isn't reliably populated with real wording for every
// failure code (DocumentProcessingTimedOut in particular) - sometimes it's just an identifier.
// This rejects anything description-shaped like a bare GUID before trusting it as a headline,
// falling through to FRIENDLY_ERROR_MESSAGES_BY_CODE/the generic message instead.
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isCustomerSafeFailureText = (text: string | null | undefined): text is string => (
    !!text && text.trim().length > 0 && !GUID_PATTERN.test(text.trim())
);

// Explicit bug report: "No analysis is available yet" shown despite the reload query genuinely
// returning assessmentJson. Root cause - the terminal subscription event is applied first (it has
// the real generation/revision but no assessmentJson, which the subscription never carries), then
// the reload it triggers fetches that SAME exact generation/revision, this time WITH
// assessmentJson. A strict `revision > session.revision` rejected that reload outright as "not
// newer" (it's equal, by design - same row), so the JSON it carried was silently discarded.
// `>=` lets an equal-tied record still merge in whatever new fields it has (assessmentJson here),
// while still rejecting anything genuinely older (a stale/out-of-order replay) via the revision
// check, or anything from an earlier generation entirely via the first check.
const isNewerRecord = (session: DocumentAiSessionBase | undefined, generation: number, revision: number): boolean => (
    !session || generation > session.generation || (generation === session.generation && revision >= session.revision)
);

// Shared shape applyAssessmentRecord merges into a session - built once from the hydration/reload
// query's own record (toApplyAssessmentRecord below) or inline from the live subscription's
// slightly differently-typed `assessment` payload (same field names, but it has no
// needsAssessment and carries its own extra failureDescription).
interface ApplyAssessmentRecordInput {
    enquiryGuid: string;
    generation: number;
    revision: number;
    isTerminal: boolean;
    hasResults: boolean;
    progressPercentage: number | null;
    progressMessage: string | null;
    isProgressIndeterminate: boolean;
    documentsExtracting: number | null;
    documentsAnalysing: number | null;
    documentsReviewing: number | null;
    passagesCompleted: number | null;
    passagesTotal: number | null;
    lastProgressAtUtc: string | null;
    documentsTotal: number | null;
    documentsCompleted: number | null;
    documentsFailed: number | null;
    stage: string | null;
    processingStatus: EnquiryAssessmentProcessingStatus;
    errorCode: string | null;
    createdAtUtc: string;
    completedAtUtc: string | null;
    // Only ever supplied by the hydration/reload query - the progress subscription's own
    // payload doesn't carry these fields, so leaving them undefined there keeps whatever the
    // session already had instead of clobbering it with null on every progress tick.
    assessmentJson?: string | null;
    citationsJson?: string | null;
    // Query-only (see DocumentAiAssessmentSession's own comment on needsAssessment) - also left
    // undefined by the subscription path, same reasoning as assessmentJson/citationsJson above.
    needsAssessment?: boolean | null;
    // Only ever supplied by the live subscription (enquiryAIProgress's own top-level
    // `description`, sibling to `assessment`) - the backend's own already customer-safe
    // explanation of a failure (e.g. "Enquiry is unavailable or access is denied."), preferred
    // over FRIENDLY_ERROR_MESSAGES_BY_CODE/the generic fallback when present. The hydration/
    // reload query has no equivalent field, so a reload after the fact falls back to one of
    // those rather than losing the specific wording already shown.
    failureDescription?: string | null;
}

// The hydration/reload query's own record, mapped into the shape applyAssessmentRecord expects -
// shared by both the hydration effect and requestAssessment's own click-time reload, so a field
// added to one path can't quietly go missing from the other.
const toApplyAssessmentRecord = (enquiryGuid: string, record: EnquiryAIAssessmentRecord): ApplyAssessmentRecordInput => ({
    enquiryGuid,
    generation: record.generation,
    revision: record.revision,
    isTerminal: record.isTerminal,
    hasResults: record.hasResults,
    progressPercentage: record.progressPercentage,
    progressMessage: record.progressMessage,
    isProgressIndeterminate: record.isProgressIndeterminate,
    documentsExtracting: record.documentsExtracting,
    documentsAnalysing: record.documentsAnalysing,
    documentsReviewing: record.documentsReviewing,
    passagesCompleted: record.passagesCompleted,
    passagesTotal: record.passagesTotal,
    lastProgressAtUtc: record.lastProgressAtUtc,
    documentsTotal: record.documentsTotal,
    documentsCompleted: record.documentsCompleted,
    documentsFailed: record.documentsFailed,
    stage: record.stage,
    processingStatus: record.processingStatus,
    errorCode: record.errorCode,
    createdAtUtc: record.createdAtUtc,
    completedAtUtc: record.completedAtUtc,
    assessmentJson: record.assessmentJson,
    citationsJson: record.citationsJson,
    needsAssessment: record.needsAssessment,
});

interface AiDocumentChatContextValue {
    sessions: DocumentAiSession[];
    activeSessionKey: string | null;
    /** Dispatches assessEnquiryAI (refresh:false) or reassessEnquiryAI (refresh:true) directly
     * and opens the session maximized, optimistically. Explicit flow change: this used to also
     * accept a documentGuid to process one document on its own, but that per-document flow was
     * removed entirely (no more per-document buttons/sessions - see this file's own comment on
     * DocumentAiSession). A session already mid-processing for this enquiry is left alone rather
     * than re-fired ("repeated clicks during the same active assessment still join that
     * operation"). Called directly only by: the in-dialog explicit "Reassess" button
     * (refresh:true, always, never from the header badge), and a failed run's own retry
     * (replaying session.lastDispatchWasReassess). The header badge's own click goes through
     * requestAssessment below instead, which decides refresh/whether to call this at all from a
     * fresh read rather than assuming local state is still current. */
    startProcessing: (enquiryGuid: string, title: string, enquirySummary: DocumentAiEnquirySummary, refresh?: boolean) => void;
    /** The enquiry's "AI Assessment" button's own click handler. Always does a fresh network-
     * only read of enquiryAIAssessment first (never trusts a possibly-stale local session, since
     * needsAssessment only means anything right after a reload) and opens whatever it finds
     * maximized; only calls startProcessing's incremental assessEnquiryAI (never reassessEnquiryAI
     * - that's reserved for the in-dialog button) when there's no assessment yet, or the backend
     * says needsAssessment: true. Mounting/reopening the host component must never call this on
     * its own - only an explicit click should. */
    requestAssessment: (enquiryGuid: string, title: string, enquirySummary: DocumentAiEnquirySummary) => void;
    /** Seeds session state from the backend's own current enquiry-level assessment status -
     * called once (per enquiryGuid, per app session) whenever the AI header badge mounts, so an
     * assessment already processing/already ready (a previous session) shows correctly without
     * the user needing to click anything first. A read only, same as requestAssessment - never
     * dispatches a mutation on its own. */
    ensureStatusChecked: (enquiryGuid: string, enquirySummary: DocumentAiEnquirySummary) => void;
    openSession: (key: string) => void;
    minimizeSession: (key: string) => void;
    closeSession: (key: string) => void;
    /** Asks documentAIQuestion against every processed document on the enquiry. A fresh
     * operationGuid is generated per new question; pass retryOperationGuid to resend an
     * already-sent question that failed to transport, reusing its exact id instead of appending
     * a duplicate message under a new one. */
    sendMessage: (key: string, message: string, retryOperationGuid?: string) => void;
    /** Loads this session's saved chat history (documentAIInteractions) once, the first time its
     * dialog is opened - not part of ensureStatusChecked's own enquiry-open hydration, since
     * history is only needed once a person actually opens a chat, not for every AI trigger badge
     * on screen. */
    ensureChatHistoryLoaded: (key: string, enquiryGuid: string) => void;
    getAssessmentSession: (enquiryGuid: string) => DocumentAiAssessmentSession | undefined;
    /** True while a status read for this enquiryGuid is in flight and no session exists yet to
     * show instead (ensureStatusChecked's own mount-time check, or requestAssessment's click-time
     * one) - explicit request: clicking the header badge felt "stuck" with no feedback while that
     * read was in flight, since there was nothing yet to distinguish "never assessed" from "still
     * checking". Deliberately not true once a session exists - that session's own processing/
     * ready/failed state already covers the UI from that point on. */
    isAssessmentChecking: (enquiryGuid: string) => boolean;
    /** True while this session's saved chat history (documentAIInteractions) is in flight,
     * triggered by ensureChatHistoryLoaded - explicit request: the conversation "just appeared
     * suddenly" with no loading state in between opening the dialog and the history actually
     * landing. */
    isChatHistoryLoading: (key: string) => boolean;
}

const AiDocumentChatContext = React.createContext<AiDocumentChatContextValue | null>(null);

export const useAiDocumentChat = (): AiDocumentChatContextValue => {
    const context = useContext(AiDocumentChatContext);
    if (!context) {
        throw new Error('useAiDocumentChat must be used within an AiDocumentChatProvider');
    }
    return context;
};

export const AiDocumentChatProvider: React.FC = ({ children }) => {
    const [sessions, setSessions] = useState<DocumentAiSession[]>([]);
    const [activeSessionKey, setActiveSessionKey] = useState<string | null>(null);
    // Explicit bug report: the header badge's click felt "stuck" - requestAssessment's own fresh
    // read is async, and there was nothing to show while it's in flight before a session exists.
    // State (not a ref) since the header badge needs to re-render as this changes.
    const [checkingEnquiries, setCheckingEnquiries] = useState<Set<string>>(new Set());
    const markChecking = useCallback((enquiryGuid: string) => {
        setCheckingEnquiries(prev => (prev.has(enquiryGuid) ? prev : new Set(prev).add(enquiryGuid)));
    }, []);
    const clearChecking = useCallback((enquiryGuid: string) => {
        setCheckingEnquiries(prev => {
            if (!prev.has(enquiryGuid)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(enquiryGuid);
            return next;
        });
    }, []);
    // Dedupes the terminal-event reload trigger (see the enquiry assessment progress subscription
    // effect below) per `${enquiryGuid}:${generation}`, so a duplicate/replayed terminal event for
    // the same run doesn't re-fetch the assessment query repeatedly.
    const terminalReloadedRef = useRef<Set<string>>(new Set());
    // Last-known enquiry identity per enquiryGuid, from whichever call site (startProcessing or
    // ensureStatusChecked) last saw it - the fallback a brand new session uses when it's created
    // with no sibling session yet to copy it from (e.g. the very first status row a fresh
    // hydration query returns).
    const enquirySummariesRef = useRef<Map<string, DocumentAiEnquirySummary>>(new Map());

    const [assessEnquiry] = useMutation<AssessEnquiryAIResult, EnquiryAIAssessmentMutationParams>(AssessEnquiryAIMutation);
    const [reassessEnquiry] = useMutation<ReassessEnquiryAIResult, EnquiryAIAssessmentMutationParams>(ReassessEnquiryAIMutation);

    // Saved chat history - fetched once per session key (chatHistoryLoadedRef), only once that
    // session's own dialog actually opens (see ensureChatHistoryLoaded), not as part of the
    // enquiry-open hydration ensureStatusChecked already does.
    const [fetchInteractions, interactionsQuery] = useLazyQuery<DocumentAIInteractionsData, DocumentAIInteractionsParams>(DocumentAIInteractionsQuery, {
        fetchPolicy: 'network-only',
    });
    const chatHistoryLoadedRef = useRef<Set<string>>(new Set());
    const interactionsSessionKeyRef = useRef<string | null>(null);
    // Explicit bug report: the saved conversation "just appeared suddenly" with no loading
    // state while documentAIInteractions was in flight. State (not a ref) so the dialog
    // re-renders as this changes; a dedicated ref below (not interactionsSessionKeyRef, which
    // the "Saved chat history" effect nulls out itself once it successfully applies a response)
    // tracks which key to clear once loading settles, regardless of whether that response ends
    // up with real history, an empty result or an error.
    const [loadingHistoryKeys, setLoadingHistoryKeys] = useState<Set<string>>(new Set());
    const historyLoadingKeyRef = useRef<string | null>(null);

    // Explicit instruction (still applies to this one remaining query): must not land in the
    // same HTTP request as enquiry.detail's own query, so a slow enquiry.detail doesn't hold up
    // AI status. Same batchGroup escape hatch YourEnquiriesWidget.tsx/EnquiryFollowUpsDueWidget.
    // tsx use for the same reason.
    const [fetchAssessment, assessmentQuery] = useLazyQuery<GetEnquiryAIAssessmentData, GetEnquiryAIAssessmentParams>(GetEnquiryAIAssessmentQuery, {
        fetchPolicy: 'network-only',
        context: { batchGroup: 'documentAi-assessment' },
    });

    // Clears checkingEnquiries for ensureStatusChecked's own fetchAssessment call the moment it
    // settles - watching `loading` itself (not `data`) so this fires whether the enquiry turns
    // out to have never been assessed (data stays null, a genuinely resolved answer) or not.
    useEffect(() => {
        const enquiryGuid = assessmentQuery.variables?.enquiryGuid;
        if (!assessmentQuery.loading && enquiryGuid) {
            clearChecking(enquiryGuid);
        }
    }, [assessmentQuery.loading, assessmentQuery.variables, clearChecking]);

    const applyAssessmentRecord = useCallback((record: ApplyAssessmentRecordInput, options?: { forceOpen?: boolean }) => {
        const key = buildAssessmentSessionKey(record.enquiryGuid);

        setSessions(prev => {
            const existing = prev.find(session => session.key === key) as DocumentAiAssessmentSession | undefined;
            if (!isNewerRecord(existing, record.generation, record.revision)) {
                // Still force the dialog open even when this exact record was already applied
                // (e.g. requestAssessment's own direct call racing the hydration effect's - see
                // its own comment) - only the open/minimize state changes, never the data.
                if (options?.forceOpen && existing && existing.isMinimized) {
                    return prev.map(session => (session.key === key ? { ...session, isMinimized: false, isActiveChip: true } : session));
                }
                return prev;
            }

            const base: DocumentAiAssessmentSession = existing ?? {
                kind: 'assessment',
                key,
                enquiryGuid: record.enquiryGuid,
                title: 'All documents',
                enquirySummary: enquirySummariesRef.current.get(record.enquiryGuid) ?? { name: 'Enquiry', email: null, areaOfLaw: null },
                isTerminal: false,
                hasResults: false,
                errorMessage: null,
                errorCode: null,
                progressPercent: null,
                stage: null,
                progressMessage: null,
                isProgressIndeterminate: false,
                lastProgressAtUtc: null,
                createdAtUtc: null,
                completedAtUtc: null,
                processingStatus: null,
                generation: 0,
                revision: 0,
                assessmentJson: null,
                citationsJson: null,
                isMinimized: true,
                messages: [],
                isSendingMessage: false,
                documentsTotal: null,
                documentsCompleted: null,
                documentsFailed: null,
                documentsExtracting: null,
                documentsAnalysing: null,
                documentsReviewing: null,
                passagesCompleted: null,
                passagesTotal: null,
                needsAssessment: null,
                lastDispatchWasReassess: false,
                isActiveChip: false,
            };

            const assessmentFailed = record.isTerminal && !record.hasResults;
            const merged: DocumentAiAssessmentSession = {
                ...base,
                isTerminal: record.isTerminal,
                hasResults: record.hasResults,
                errorMessage: assessmentFailed
                    ? (isCustomerSafeFailureText(record.failureDescription) ? record.failureDescription : null)
                        || (record.errorCode && FRIENDLY_ERROR_MESSAGES_BY_CODE[record.errorCode])
                        || ASSESSMENT_AI_ERROR_MESSAGE
                    : null,
                errorCode: assessmentFailed ? record.errorCode : null,
                progressPercent: record.progressPercentage,
                stage: record.stage,
                progressMessage: record.progressMessage,
                isProgressIndeterminate: record.isProgressIndeterminate,
                lastProgressAtUtc: record.lastProgressAtUtc,
                createdAtUtc: record.createdAtUtc,
                completedAtUtc: record.completedAtUtc,
                processingStatus: record.processingStatus,
                generation: record.generation,
                revision: record.revision,
                assessmentJson: record.assessmentJson !== undefined ? record.assessmentJson : base.assessmentJson,
                citationsJson: record.citationsJson !== undefined ? record.citationsJson : base.citationsJson,
                needsAssessment: record.needsAssessment !== undefined ? record.needsAssessment : base.needsAssessment,
                documentsTotal: record.documentsTotal,
                documentsCompleted: record.documentsCompleted,
                documentsFailed: record.documentsFailed,
                documentsExtracting: record.documentsExtracting,
                documentsAnalysing: record.documentsAnalysing,
                documentsReviewing: record.documentsReviewing,
                passagesCompleted: record.passagesCompleted,
                passagesTotal: record.passagesTotal,
                isMinimized: options?.forceOpen ? false : base.isMinimized,
                isActiveChip: options?.forceOpen ? true : base.isActiveChip,
            };

            return existing
                ? prev.map(session => (session.key === key ? merged : session))
                : [...prev, merged];
        });
    }, []);

    // ── Enquiry assessment query (hydration) ─────────────────────────────────
    useEffect(() => {
        const record = assessmentQuery.data?.crm?.enquiryAIAssessment;
        const enquiryGuid = assessmentQuery.variables?.enquiryGuid;
        if (!record || !enquiryGuid) {
            return;
        }

        applyAssessmentRecord(toApplyAssessmentRecord(enquiryGuid, record));
    // tslint:disable-next-line: align
    }, [assessmentQuery.data, assessmentQuery.variables, applyAssessmentRecord]);

    // Clears loadingHistoryKeys the moment this query settles, watching `loading` itself (not
    // `data`) so it fires whether the response carries real history, an empty result, or fails
    // outright - a stuck "loading" state is worse than an empty one.
    useEffect(() => {
        if (!interactionsQuery.loading && historyLoadingKeyRef.current) {
            const settledKey = historyLoadingKeyRef.current;
            historyLoadingKeyRef.current = null;
            setLoadingHistoryKeys(prev => {
                if (!prev.has(settledKey)) {
                    return prev;
                }
                const next = new Set(prev);
                next.delete(settledKey);
                return next;
            });
        }
    }, [interactionsQuery.loading]);

    // ── Saved chat history ───────────────────────────────────────────────────
    useEffect(() => {
        const key = interactionsSessionKeyRef.current;
        const interactions = interactionsQuery.data?.crm?.documentAIInteractions;
        if (!key || !interactions) {
            return;
        }
        interactionsSessionKeyRef.current = null;

        // Oldest first for display - the query's own ordering isn't guaranteed, so sort
        // explicitly rather than assume.
        const sorted = [...interactions].sort((a, b) => new Date(a.createdAtUtc).getTime() - new Date(b.createdAtUtc).getTime());

        const historyMessages: DocumentAiChatMessage[] = sorted.flatMap(interaction => {
            const userMessage: DocumentAiChatMessage = {
                id: `${interaction.interactionGuid}-question`,
                role: 'user',
                content: interaction.question,
                createdAt: interaction.createdAtUtc,
            };
            // status: 0 processing, 1 answered, 2 failed - a still-processing saved interaction
            // (e.g. the tab was closed mid-answer) has no response yet, so it's shown as just the
            // question rather than a blank/misleading assistant bubble.
            if (interaction.status === 2) {
                return [userMessage, {
                    id: `${interaction.interactionGuid}-response`,
                    role: 'assistant',
                    content: "Sorry, that question couldn't be answered.",
                    createdAt: interaction.createdAtUtc,
                    isError: true,
                }];
            }
            if (interaction.status === 1 && interaction.response) {
                return [userMessage, {
                    id: `${interaction.interactionGuid}-response`,
                    role: 'assistant',
                    content: interaction.response,
                    createdAt: interaction.createdAtUtc,
                    citations: interaction.citations,
                }];
            }
            return [userMessage];
        });

        setSessions(prev => prev.map(session => (
            session.key === key && session.messages.length === 0
                ? { ...session, messages: historyMessages }
                : session
        )));
    // tslint:disable-next-line: align
    }, [interactionsQuery.data]);

    // ── Enquiry assessment progress subscription ─────────────────────────────
    // Explicit flow change: this used to be a secondary stream alongside a global per-document
    // documentAIProgress subscription (removed entirely - no more per-document sessions/pills,
    // see DocumentAiSession's own comment). This is now the only live progress source.
    //
    // Explicit request: this Provider is mounted globally for every authenticated user
    // (AppContent.tsx), regardless of hasCRMEnquiryAssessmentAI - only the UI entry points
    // (EnquiryAiHeaderBadge's own render site) were gated by that feature so far. Without `skip`
    // here, a user lacking the feature would still have this subscription silently established,
    // and any terminal event it received would still create a local session and fire
    // GetEnquiryAIAssessment for it - skip stops the subscription itself from ever being made for
    // such a user, closing that gap at its root instead of gating every effect downstream of it.
    const enquiryProgressSubscription = useSubscription<EnquiryAIProgressSubscriptionData>(EnquiryAIProgressSubscription, {
        skip: !UserFeature.HasAccess(UserFeatureEnum.hasCRMEnquiryAssessmentAI),
    });

    useEffect(() => {
        const assessment = enquiryProgressSubscription.data?.enquiryAIProgress?.assessment;
        if (!assessment) {
            return;
        }

        applyAssessmentRecord({
            enquiryGuid: assessment.entityGuid,
            generation: assessment.generation,
            revision: assessment.revision,
            isTerminal: assessment.isTerminal,
            hasResults: assessment.hasResults,
            progressPercentage: assessment.progressPercentage,
            progressMessage: assessment.progressMessage,
            isProgressIndeterminate: assessment.isProgressIndeterminate,
            documentsExtracting: assessment.documentsExtracting,
            documentsAnalysing: assessment.documentsAnalysing,
            documentsReviewing: assessment.documentsReviewing,
            passagesCompleted: assessment.passagesCompleted,
            passagesTotal: assessment.passagesTotal,
            lastProgressAtUtc: assessment.lastProgressAtUtc,
            documentsTotal: assessment.documentsTotal,
            documentsCompleted: assessment.documentsCompleted,
            documentsFailed: assessment.documentsFailed,
            stage: assessment.stage,
            processingStatus: assessment.processingStatus,
            errorCode: assessment.errorCode,
            createdAtUtc: assessment.createdAtUtc,
            completedAtUtc: assessment.completedAtUtc,
            failureDescription: enquiryProgressSubscription.data?.enquiryAIProgress?.description ?? null,
        });

        // The subscription payload never carries assessmentJson/citationsJson (see
        // EnquiryAIProgressEvent's own comment) - a terminal event reloads the full saved
        // assessment so the just-finished run's own JSON actually lands in the session, instead
        // of leaving it null until something else happens to reopen the panel. Deduped per
        // generation so a duplicate/replayed terminal event doesn't re-fetch needlessly.
        //
        // Explicit bug report: "No analysis is available yet" flashed for a second or two right
        // as a run finished. Root cause - this terminal event above already flips isTerminal/
        // hasResults to ready immediately (displayState becomes 'ready'), but assessmentJson
        // itself only lands once THIS reload resolves, slightly later - in that gap, the dialog
        // genuinely has nothing to parse yet, so it showed the "nothing's there" fallback as if
        // that were the final state. markChecking (already used for the header badge's own
        // loading state - see its own comment) flags exactly this gap, so the dialog can show a
        // brief loading state instead of "no analysis" until this specific reload settles.
        if (assessment.isTerminal) {
            const reloadKey = `${assessment.entityGuid}:${assessment.generation}`;
            if (!terminalReloadedRef.current.has(reloadKey)) {
                terminalReloadedRef.current.add(reloadKey);
                markChecking(assessment.entityGuid);
                fetchAssessment({ variables: { enquiryGuid: assessment.entityGuid } });
            }
        }
    // tslint:disable-next-line: align
    }, [enquiryProgressSubscription.data, applyAssessmentRecord, fetchAssessment, markChecking]);

    const startProcessing = useCallback((
        enquiryGuid: string,
        title: string,
        enquirySummary: DocumentAiEnquirySummary,
        refresh: boolean = false,
    ) => {
        const key = buildAssessmentSessionKey(enquiryGuid);
        enquirySummariesRef.current.set(enquiryGuid, enquirySummary);

        let alreadyProcessing = false;
        setSessions(prev => {
            const existing = prev.find(session => session.key === key) as DocumentAiAssessmentSession | undefined;
            if (existing && !existing.isTerminal) {
                alreadyProcessing = true;
                return prev;
            }

            const freshSession: DocumentAiAssessmentSession = {
                kind: 'assessment',
                key,
                enquiryGuid,
                title,
                enquirySummary,
                isTerminal: false,
                // A FULL reassessment (refresh:true) clears these immediately, matching the
                // backend's own "hides the earlier report while refreshing" behaviour for that
                // specific action - an INCREMENTAL assess instead keeps the previous result
                // viewable while this run is in flight.
                hasResults: refresh ? false : (existing?.hasResults ?? false),
                errorMessage: null,
                errorCode: null,
                progressPercent: null,
                stage: null,
                progressMessage: null,
                isProgressIndeterminate: false,
                lastProgressAtUtc: null,
                createdAtUtc: existing?.createdAtUtc ?? null,
                completedAtUtc: null,
                // Optimistic - the first real status/progress event overwrites this once the
                // backend has actually picked the run up.
                processingStatus: EnquiryAssessmentProcessingStatus.Queued,
                generation: existing?.generation ?? 0,
                revision: existing?.revision ?? 0,
                assessmentJson: refresh ? null : (existing?.assessmentJson ?? null),
                citationsJson: refresh ? null : (existing?.citationsJson ?? null),
                isMinimized: false,
                messages: existing?.messages ?? [],
                isSendingMessage: false,
                documentsTotal: existing?.documentsTotal ?? null,
                documentsCompleted: existing?.documentsCompleted ?? null,
                documentsFailed: existing?.documentsFailed ?? null,
                documentsExtracting: null,
                documentsAnalysing: null,
                documentsReviewing: null,
                passagesCompleted: null,
                passagesTotal: null,
                // Unknown again until the next reload actually answers it - never assumed true or
                // false from the mere act of dispatching this run.
                needsAssessment: null,
                lastDispatchWasReassess: refresh,
                // Always true here - startProcessing is only ever reached via a genuine user
                // action (a badge click that decided an assess is actually needed, the in-dialog
                // Reassess button, or a failed run's own retry), never silent background
                // hydration.
                isActiveChip: true,
            };

            return existing
                ? prev.map(session => (session.key === key ? freshSession : session))
                : [...prev, freshSession];
        });
        setActiveSessionKey(key);

        if (alreadyProcessing) {
            return;
        }

        const onMutationError = (rawError: string | null) => {
            // Only a genuine error changes anything here - a null/empty response just means the
            // run was accepted, and its real progress arrives via the subscription above, not
            // this mutation's own response.
            if (!rawError) {
                return;
            }
            setSessions(prev => prev.map(session => (
                session.key === key
                    ? {
                        ...session,
                        isTerminal: true,
                        errorMessage: ASSESSMENT_AI_ERROR_MESSAGE,
                        // Not shown to the user directly (same reasoning as the status/progress
                        // errorCode - unclear whether this string is friendly copy or a machine
                        // code, so treat it as the latter and keep it only for support reference).
                        errorCode: rawError,
                    }
                    : session
            )));
        };

        const onNetworkError = () => {
            setSessions(prev => prev.map(session => (
                session.key === key
                    ? { ...session, isTerminal: true, errorMessage: 'Could not start AI processing. Please try again.', errorCode: null }
                    : session
            )));
        };

        // Reload the saved status after dispatch regardless of outcome - a safety net alongside
        // the subscription above, so a progress event dropped by the transport between dispatch
        // and the first subscription tick can't leave the UI stuck on the optimistic Queued state
        // forever.
        const reloadStatus = () => fetchAssessment({ variables: { enquiryGuid } });

        // On success in both branches below, actual progress arrives via the subscription
        // above - the mutation itself only confirms the run was accepted.
        if (refresh) {
            // reassessEnquiryAI always creates a fresh generation, re-reads every attachment and
            // excludes historical findings - only ever dispatched from the in-dialog explicit
            // "Reassess" button, or a failed run's own retry replaying the same kind
            // (session.lastDispatchWasReassess). Never dispatched from the header badge's own
            // click, which always goes through requestAssessment's incremental assessEnquiryAI.
            reassessEnquiry({ variables: { enquiryGuid } })
                .then(response => onMutationError(response.data?.reassessEnquiryAI?.error ?? null))
                .catch(onNetworkError)
                .then(reloadStatus);
        } else {
            // assessEnquiryAI processes only missing, changed or failed documents and reuses
            // valid findings - the incremental "Assess"/retry-after-an-incremental-failure
            // action.
            assessEnquiry({ variables: { enquiryGuid } })
                .then(response => onMutationError(response.data?.assessEnquiryAI?.error ?? null))
                .catch(onNetworkError)
                .then(reloadStatus);
        }
    }, [assessEnquiry, reassessEnquiry, fetchAssessment]);

    // The "AI Assessment" button's own click handler - see this file's own comment on
    // requestAssessment in AiDocumentChatContextValue for the full decision rules. Always reads
    // fresh (network-only) first; only ever dispatches startProcessing's incremental
    // assessEnquiryAI (refresh:false), never reassessEnquiryAI.
    //
    // Uses client.query directly rather than the fetchAssessment/assessmentQuery lazy-query tuple
    // above - this react-apollo version's useLazyQuery execute function returns void, not a
    // Promise, so there's nothing to await from it here. client.query (the same imperative,
    // Promise-returning pattern sendMessage below already uses) reads this independently of that
    // hook's own hydration effect, and this function applies the result itself either way.
    const requestAssessment = useCallback((enquiryGuid: string, title: string, enquirySummary: DocumentAiEnquirySummary) => {
        enquirySummariesRef.current.set(enquiryGuid, enquirySummary);
        const key = buildAssessmentSessionKey(enquiryGuid);
        setActiveSessionKey(key);
        markChecking(enquiryGuid);

        // Explicit bug report: clicking a "ready" badge felt stuck, waiting on this network
        // round-trip before the dialog opened at all - even though a session with perfectly
        // good, already-loaded data (from the earlier silent ensureStatusChecked hydration) was
        // usually already sitting right here in memory. Opening immediately with whatever's
        // already known (a plain no-op if no session exists yet) means the common case - this
        // badge already showing a real state - opens instantly; the fresh read below still runs
        // to check needsAssessment, just in the background rather than blocking the open.
        setSessions(prev => prev.map(session => (session.key === key ? { ...session, isMinimized: false } : session)));

        client.query<GetEnquiryAIAssessmentData, GetEnquiryAIAssessmentParams>({
            query: GetEnquiryAIAssessmentQuery,
            variables: { enquiryGuid },
            fetchPolicy: 'network-only',
        }).then(result => {
            clearChecking(enquiryGuid);
            const record = result.data?.crm?.enquiryAIAssessment;

            // No assessment yet, or the backend's own fresh snapshot says something's changed
            // since the last saved result (a new/changed document, a failed document, edited
            // enquiry details) - dispatch. Never reached while already processing (needsAssessment
            // is null then, never true), so this can't double-fire alongside the "still running"
            // branch below.
            if (!record || record.needsAssessment === true) {
                startProcessing(enquiryGuid, title, enquirySummary, false);
                return;
            }

            // Already processing, ready with nothing changed, or terminally failed - just show
            // what's there; repeated clicks while already processing join that same operation
            // rather than dispatching a second one, and a failed state's own retry is the next
            // explicit click (same path), not auto-retried here.
            applyAssessmentRecord(toApplyAssessmentRecord(enquiryGuid, record), { forceOpen: true });
        }).catch(() => clearChecking(enquiryGuid));
    }, [startProcessing, applyAssessmentRecord, markChecking, clearChecking]);

    const ensureStatusChecked = useCallback((enquiryGuid: string, enquirySummary: DocumentAiEnquirySummary) => {
        enquirySummariesRef.current.set(enquiryGuid, enquirySummary);

        // Explicit bug report: reopening an enquiry (closing it, then opening it again in the
        // same browser tab) kept showing the idle "AI" badge even though the server already had
        // a ready result - the badge's own mount effect calls this every time it mounts (i.e.
        // every time the enquiry is opened), but a now-removed per-enquiryGuid dedup ref used to
        // make every mount AFTER the very first one in this browser session a silent no-op,
        // trusting (wrongly) that nothing could have changed since. The backend's own guidance is
        // explicit: "load enquiryAIAssessment on opening" - every opening, not just the first.
        // This query asks about this specific enquiryGuid directly, so whatever it returns is
        // inherently relevant - no placeholder session needs seeding first (a seeded "processing"
        // placeholder would show a permanently-stuck pill for every enquiry ever opened if it
        // turns out nothing's actually running).
        markChecking(enquiryGuid);
        fetchAssessment({ variables: { enquiryGuid } });
    }, [fetchAssessment, markChecking]);

    const openSession = useCallback((key: string) => {
        setSessions(prev => prev.map(session => (
            session.key === key ? { ...session, isMinimized: false, isActiveChip: true } : session
        )));
        setActiveSessionKey(key);
    }, []);

    const minimizeSession = useCallback((key: string) => {
        setSessions(prev => prev.map(session => (
            session.key === key ? { ...session, isMinimized: true } : session
        )));
        setActiveSessionKey(prevKey => (prevKey === key ? null : prevKey));
    }, []);

    const closeSession = useCallback((key: string) => {
        setSessions(prev => prev.filter(session => session.key !== key));
        setActiveSessionKey(prevKey => (prevKey === key ? null : prevKey));
        // Explicit bug report: previously-asked questions stopped showing after closing the
        // dialog and reopening it. Root cause - this wipes the session (its in-memory messages
        // included), but chatHistoryLoadedRef still remembered this key as "already loaded" from
        // before, so ensureChatHistoryLoaded's own dedup skipped the refetch the freshly
        // recreated session actually needs. Clearing it here means reopening always reloads the
        // saved history fresh, exactly like opening this enquiry's chat for the very first time.
        chatHistoryLoadedRef.current.delete(key);
    }, []);

    // Per the backend's own recommended UI call: an imperative client.query with
    // fetchPolicy 'no-cache' (this is a blocking, non-cacheable read, not something to route
    // through a React query hook's own cache-aware lifecycle) - "generate the operation ID once
    // before the request and retain it with the pending message until that exact request either
    // succeeds or the user abandons it". retryOperationGuid resends the SAME question under the
    // SAME id (a transport-level retry after a failed send, per that guidance) rather than
    // appending a second user message - a brand new question always gets a fresh id instead.
    const sendMessage = useCallback((key: string, message: string, retryOperationGuid?: string) => {
        const trimmed = message.trim();
        if (!trimmed) {
            return;
        }

        const session = sessions.find(item => item.key === key);
        if (!session) {
            return;
        }

        const operationGuid = retryOperationGuid ?? uuidv4();

        setSessions(prev => prev.map(item => {
            if (item.key !== key) {
                return item;
            }
            if (retryOperationGuid) {
                return {
                    ...item,
                    isSendingMessage: true,
                    messages: item.messages.map(existingMessage => (
                        existingMessage.operationGuid === retryOperationGuid
                            ? { ...existingMessage, sendFailed: false }
                            : existingMessage
                    )),
                };
            }
            return {
                ...item,
                isSendingMessage: true,
                messages: [...item.messages, {
                    id: `local-${Date.now()}-${Math.random()}`,
                    role: 'user' as const,
                    content: trimmed,
                    createdAt: new Date().toISOString(),
                    operationGuid,
                }],
            };
        }));

        client.query<DocumentAIQuestionData, DocumentAIQuestionParams>({
            query: DocumentAIQuestionQuery,
            variables: {
                enquiryGuid: session.enquiryGuid,
                operationGuid,
                documentGuid: null,
                question: trimmed,
            },
            fetchPolicy: 'no-cache',
        })
            .then(response => {
                const record = response.data?.crm?.documentAIQuestion;
                setSessions(prev => prev.map(item => (
                    item.key === key
                        ? {
                            ...item,
                            isSendingMessage: false,
                            messages: record ? [...item.messages, {
                                id: record.interactionGuid,
                                role: 'assistant' as const,
                                content: record.answer,
                                createdAt: record.createdAtUtc,
                                citations: record.citations,
                            }] : item.messages,
                        }
                        : item
                )));
            })
            .catch(() => {
                // A transport-level failure - flag the user's own message so the UI can offer an
                // inline retry with this same operationGuid, rather than appending a separate
                // error bubble (that's reserved for a recorded server-side failure - see
                // DocumentAIInteractionsQuery's own status: 2 handling below).
                setSessions(prev => prev.map(item => (
                    item.key === key
                        ? {
                            ...item,
                            isSendingMessage: false,
                            messages: item.messages.map(existingMessage => (
                                existingMessage.operationGuid === operationGuid
                                    ? { ...existingMessage, sendFailed: true }
                                    : existingMessage
                            )),
                        }
                        : item
                )));
            });
    }, [sessions]);

    const ensureChatHistoryLoaded = useCallback((key: string, enquiryGuid: string) => {
        if (chatHistoryLoadedRef.current.has(key)) {
            return;
        }
        chatHistoryLoadedRef.current.add(key);

        interactionsSessionKeyRef.current = key;
        historyLoadingKeyRef.current = key;
        setLoadingHistoryKeys(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
        fetchInteractions({ variables: { enquiryGuid, documentGuid: null, take: 50 } });
    }, [fetchInteractions]);

    const getAssessmentSession = useCallback((enquiryGuid: string): DocumentAiAssessmentSession | undefined => (
        sessions.find((session): session is DocumentAiAssessmentSession => session.kind === 'assessment' && session.enquiryGuid === enquiryGuid)
    ), [sessions]);

    const isAssessmentChecking = useCallback((enquiryGuid: string): boolean => (
        checkingEnquiries.has(enquiryGuid)
    ), [checkingEnquiries]);

    const isChatHistoryLoading = useCallback((key: string): boolean => (
        loadingHistoryKeys.has(key)
    ), [loadingHistoryKeys]);

    const value: AiDocumentChatContextValue = {
        sessions,
        activeSessionKey,
        startProcessing,
        requestAssessment,
        ensureStatusChecked,
        openSession,
        minimizeSession,
        closeSession,
        sendMessage,
        ensureChatHistoryLoaded,
        getAssessmentSession,
        isAssessmentChecking,
        isChatHistoryLoading,
    };

    return (
        <AiDocumentChatContext.Provider value={value}>
            {children}
        </AiDocumentChatContext.Provider>
    );
};
